import React from 'react';
import { formatAmount } from '../../../lib/money';
import { Badge, Banner, Button, Card, DescriptionList, Progress } from '../../../ui/components';
import { Icon } from '../../../ui/icons';
import { useT } from '../../../i18n';

const fmt = (v) => (v === null || v === undefined || v === '' ? '—' : v);

function money(m) {
  if (!m || m.amount == null) return '—';
  return `${m.currency || ''} ${formatAmount(m.amount)}`.trim();
}

export default function OverviewTab({ supplier, stats, onNavigateTab }) {
  const t = useT();
  const primaryContact = (supplier.contacts || []).find((c) => c.isPrimary) || (supplier.contacts || [])[0];
  const primaryAddress = (supplier.addresses || []).find((a) => a.isPrimary) || (supplier.addresses || [])[0];

  const company = [
    { term: t('Legal name'), description: fmt(supplier.legalName) },
    { term: t('Trade name'), description: fmt(supplier.tradeName) },
    { term: t('Category'), description: fmt(supplier.category) },
    { term: t('Website'), description: supplier.website
      ? <a href={supplier.website} target="_blank" rel="noopener noreferrer">{supplier.website}</a> : '—' },
    { term: t('Rating'), description: supplier.rating ? `${supplier.rating} / 5` : '—' },
  ];

  const commercial = [
    { term: t('Currency'), description: fmt(supplier.currency) },
    { term: t('Payment terms'), description: supplier.paymentTermsDays != null ? t('{count} days', { count: supplier.paymentTermsDays }) : '—' },
    { term: t('Lead time'), description: supplier.defaultLeadTimeDays != null ? t('{count} days', { count: supplier.defaultLeadTimeDays }) : '—' },
    { term: t('Incoterms'), description: fmt(supplier.incoterms) },
    { term: t('Payment method'), description: fmt(supplier.preferredPaymentMethod) },
    { term: t('Min. order'), description: money(supplier.minimumOrderValue) },
  ];

  const legal = [
    { term: t('Tax ID'), description: fmt(supplier.taxId) },
    { term: t('Registration no.'), description: fmt(supplier.registrationNumber) },
    { term: 'Prostay Accounting', description: fmt(supplier.prostayAccountingSupplierId) },
    { term: 'TableView', description: fmt(supplier.tableViewSupplierId) },
  ];

  return (
    <div className="sp-panel">
      {stats && stats.profileCompleteness < 100 && (
        <Banner
          tone="info"
          title={t('Profile is {percent}% complete', { percent: stats.profileCompleteness })}
          action={<Button variant="ghost" size="sm" onClick={() => onNavigateTab('profile')}>{t('Complete profile')}</Button>}
        >
          <Progress value={stats.profileCompleteness} max={100} />
        </Banner>
      )}

      {supplier.description && (
        <Card>
          <Card.Header><Card.Title>{t('About')}</Card.Title></Card.Header>
          <Card.Body><p className="sp-muted" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{supplier.description}</p></Card.Body>
        </Card>
      )}

      <div className="sp-grid-2">
        <Card>
          <Card.Header><Card.Title>{t('Company')}</Card.Title></Card.Header>
          <Card.Body><DescriptionList items={company} /></Card.Body>
        </Card>

        <Card>
          <Card.Header><Card.Title>{t('Commercial terms')}</Card.Title></Card.Header>
          <Card.Body><DescriptionList items={commercial} /></Card.Body>
        </Card>

        <Card>
          <Card.Header><Card.Title>{t('Compliance & links')}</Card.Title></Card.Header>
          <Card.Body><DescriptionList items={legal} /></Card.Body>
        </Card>

        <Card>
          <Card.Header><Card.Title>{t('Primary contact & address')}</Card.Title></Card.Header>
          <Card.Body>
            {primaryContact ? (
              <div className="sp-item__main" style={{ marginBottom: 'var(--space-3)' }}>
                <span className="sp-item__title">
                  <Icon.User size={15} /> {primaryContact.name}
                  {primaryContact.title && <Badge tone="neutral">{primaryContact.title}</Badge>}
                </span>
                <span className="sp-item__meta">
                  {primaryContact.email && <span><Icon.Mail size={13} /> {primaryContact.email}</span>}
                  {primaryContact.phone && <span><Icon.Phone size={13} /> {primaryContact.phone}</span>}
                </span>
              </div>
            ) : <p className="sp-muted" style={{ marginTop: 0 }}>{t('No contact yet.')}</p>}

            {primaryAddress ? (
              <div className="sp-item__main">
                <span className="sp-item__title"><Icon.MapPin size={15} /> {t(primaryAddress.type)}</span>
                <span className="sp-item__meta">
                  {[primaryAddress.line1, primaryAddress.city, primaryAddress.state, primaryAddress.country]
                    .filter(Boolean).join(', ') || '—'}
                </span>
              </div>
            ) : <p className="sp-muted" style={{ marginBottom: 0 }}>{t('No address yet.')}</p>}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}
